import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import CircularProgress from '@mui/material/CircularProgress';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import SearchIcon from '@mui/icons-material/Search';
import Button from '@mui/material/Button';
import AddIcon from '@mui/icons-material/Add';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Alert from '@mui/material/Alert';
import Tooltip from '@mui/material/Tooltip';
import ReactVirtualizedTable from '../../../SystemDesign/table';
import { useAuth } from '../../../context/AuthContext';
import { useAbility } from '../../../permissions/AbilityContext';
import Notification from '../../common/Notification';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import theme from '../../../theme';
import ProfessionalClientDetailsDialog from './ProfessionalClientDetailsDialog';

import {
  useUsersClientsByOwnerListQuery,
  useUsersClientsBySalonListQuery,
  useSalonsListQuery,
  useAuthAddclientCreateMutation,
  useUsersNotifyClientsCreateMutation,
} from '../../../services/GLOWClient';


function SectionHeader({ title }) {
  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="h6" sx={{ fontWeight: 600 }}>{title}</Typography>
      <Divider sx={{ mt: 1 }} />
    </Box>
  );
}

function LoadingState() {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 6 }}>
      <CircularProgress size={28} />
    </Box>
  );
}

function OwnerClientsTable({ ownerId }) {
  const { t } = useTranslation();
  
  const columns = useMemo(() => [
    { width: 140, label: t('pro_clients.columns.first_name'), dataKey: 'first_name' },
    { width: 160, label: t('pro_clients.columns.last_name'), dataKey: 'last_name' },
    { width: 240, label: t('pro_clients.columns.email'), dataKey: 'email' },
    { width: 160, label: t('pro_clients.columns.phone'), dataKey: 'phone' },
    { width: 180, label: t('pro_clients.columns.salon'), dataKey: 'salon_name' },
  ], [t]);

  const { data, isLoading, isFetching } = useUsersClientsByOwnerListQuery(
    { ownerId: Number(ownerId) },
    { skip: !ownerId }
  );

  if (isLoading || isFetching) return <LoadingState />;

  return (
    <ReactVirtualizedTable
      data={Array.isArray(data) ? data : []}
      columns={columns}
      height={420}
      showActions={false}
    />
  );
}

export default function ClientData() {
  const { t } = useTranslation();
  const { user } = useAuth();

  const columns = useMemo(() => [
    { width: 140, label: t('pro_clients.columns.first_name'), dataKey: 'first_name' },
    { width: 160, label: t('pro_clients.columns.last_name'), dataKey: 'last_name' },
    { width: 240, label: t('pro_clients.columns.email'), dataKey: 'email' },
    { width: 160, label: t('pro_clients.columns.phone'), dataKey: 'phone' },
    { width: 180, label: t('pro_clients.columns.salon'), dataKey: 'salon_name' },
  ], [t]);
  const ability = useAbility();
  const ownerId = user?.id;

  const isRichTextEmpty = (html) => {
    if (!html) return true;
    const text = String(html)
      .replace(/<[^>]*>/g, '')
      .replace(/\u00a0/g, ' ')
      .trim();
    return text.length === 0;
  };

  const quillModules = useMemo(
    () => ({
      toolbar: [
        [{ header: [1, 2, 3, false] }],
        ['bold', 'italic', 'underline', 'strike'],
        [{ color: [] }, { background: [] }],
        [{ list: 'ordered' }, { list: 'bullet' }],
        [{ align: [] }],
        ['link'],
        ['clean'],
      ],
    }),
    []
  );

  const quillFormats = useMemo(
    () => [
      'header',
      'bold',
      'italic',
      'underline',
      'strike',
      'color',
      'background',
      'list',
      'bullet',
      'align',
      'link',
    ],
    []
  );

  // Fetch all salons for this owner
  const { data: salons, isLoading: salonsLoading, isFetching: salonsFetching } = useSalonsListQuery(
    { owner: Number(ownerId) },
    { skip: !ownerId }
  );

  const [mode, setMode] = useState('owner'); // 'owner' | 'salon'
  const [selectedSalonId, setSelectedSalonId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [detailsClient, setDetailsClient] = useState(null);

  // Add Client Modal State
  const [isAddClientOpen, setIsAddClientOpen] = useState(false);
  const [newClientFirstName, setNewClientFirstName] = useState('');
  const [newClientLastName, setNewClientLastName] = useState('');
  const [newClientEmail, setNewClientEmail] = useState('');
  const [newClientPhone, setNewClientPhone] = useState('');
  const [addClientError, setAddClientError] = useState(null);

  const [addClient, { isLoading: isAddingClient }] = useAuthAddclientCreateMutation();
  const [notifyClients, { isLoading: isNotifying }] = useUsersNotifyClientsCreateMutation();

  // Selection state
  const [selectedClientIds, setSelectedClientIds] = useState([]);

  // Notify Clients Modal State
  const [isNotifyDialogOpen, setIsNotifyDialogOpen] = useState(false);
  const [sendEmail, setSendEmail] = useState(true);
  const [sendNotification, setSendNotification] = useState(true);
  const [notifySalonId, setNotifySalonId] = useState('');
  const [emailSubject, setEmailSubject] = useState('');
  const [emailContent, setEmailContent] = useState('');
  const [notificationTitle, setNotificationTitle] = useState('');
  const [notificationMessage, setNotificationMessage] = useState('');
  const [photo, setPhoto] = useState(null);
  const [notifyError, setNotifyError] = useState(null);

  const [notificationState, setNotificationState] = useState({
    open: false,
    message: '',
    severity: 'success',
  });

  const handleOpenAddClient = () => {
    setNewClientFirstName('');
    setNewClientLastName('');
    setNewClientEmail('');
    setNewClientPhone('');
    setAddClientError(null);
    setIsAddClientOpen(true);
  };

  const handleCloseAddClient = () => {
    setIsAddClientOpen(false);
  };

  const handleAddClientSubmit = async (e) => {
    e.preventDefault();
    setAddClientError(null);

    if (!newClientFirstName || !newClientLastName || !newClientEmail || !newClientPhone) {
      setAddClientError(t('pro_clients.validation.all_fields_required'));
      return;
    }

    try {
      await addClient({
        simpleClientRequest: {
          first_name: newClientFirstName,
          last_name: newClientLastName,
          email: newClientEmail,
          phone: newClientPhone || undefined,
        }
      }).unwrap();

      handleCloseAddClient();
    } catch (err) {
      console.error('Failed to add client:', err);
      setAddClientError(err.data?.detail || err.data?.email?.[0] || 'Une erreur est survenue lors de l\'ajout du client.');
    }
  };

  const handleOpenNotifyDialog = () => {
    if (selectedClientIds.length === 0) {
      alert(t('pro_clients.validation.select_at_least_one'));
      return;
    }
    setEmailSubject('');
    setEmailContent('');
    setNotificationTitle('');
    setNotificationMessage('');
    setPhoto(null);
    setSendEmail(true);
    setSendNotification(true);
    setNotifyError(null);
    if (salonOptions.length === 1) {
      setNotifySalonId(salonOptions[0].salon_id);
    } else if (selectedSalonId) {
      setNotifySalonId(selectedSalonId);
    } else {
      setNotifySalonId('');
    }
    setIsNotifyDialogOpen(true);
  };

  const handleCloseNotifyDialog = () => {
    setIsNotifyDialogOpen(false);
  };

  const handleNotifySubmit = async (e) => {
    e.preventDefault();
    setNotifyError(null);

    const deliveryModes = [];
    if (sendEmail) deliveryModes.push('email');
    if (sendNotification) deliveryModes.push('notification');

    if (deliveryModes.length === 0) {
      setNotifyError('Veuillez sélectionner au moins un mode d\'envoi (Email ou Notification).');
      return;
    }

    if (sendEmail && (!emailSubject || isRichTextEmpty(emailContent))) {
      setNotifyError('Veuillez remplir le sujet et le contenu de l\'email.');
      return;
    }

    if (sendNotification && (!notificationTitle || isRichTextEmpty(notificationMessage))) {
      setNotifyError(t('pro_clients.validation.fill_notification_fields'));
      return;
    }

    if (salonOptions.length > 1 && !notifySalonId) {
      setNotifyError(t('pro_clients.validation.select_sender_salon'));
      return;
    }

    try {
      const result = await notifyClients({
        clientIds: selectedClientIds,
        deliveryModes,
        salonId: notifySalonId || undefined,
        emailSubject,
        emailContent,
        notificationTitle,
        notificationMessage,
        photo,
      }).unwrap();

      const emailsSent = Number(result?.email_sent ?? 0);
      const notificationsCreated = Number(result?.notifications_created ?? 0);
      setNotificationState({
        open: true,
        severity: 'success',
        message: t('pro_clients.notify.success', { emailsSent, notificationsCreated }),
      });

      if (Array.isArray(result?.errors) && result.errors.length > 0) {
        setNotificationState({
          open: true,
          severity: 'warning',
          message: t('pro_clients.notify.partial_success', { emailsSent, notificationsCreated, errors: result.errors.join(', ') }),
        });
      }
      handleCloseNotifyDialog();
      setSelectedClientIds([]);
    } catch (err) {
      console.error('Failed to notify clients:', err);
      const msg = err.data?.detail || 'Une erreur est survenue lors de l\'envoi des notifications.';
      setNotifyError(msg);
      setNotificationState({ open: true, severity: 'error', message: msg });
    }
  };

  const handlePhotoChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      setPhoto(e.target.files[0]);
    }
  };

  // Prepare list of salons options
  const salonOptions = useMemo(() => (Array.isArray(salons) ? salons : []), [salons]);

  // Initialize selected salon once salons are loaded
  useEffect(() => {
    if (!selectedSalonId && salonOptions.length > 0) {
      setSelectedSalonId(salonOptions[0].salon_id);
    }
  }, [selectedSalonId, salonOptions]);

  // Prepare search parameters
  const searchParams = useMemo(() => {
    const params = {};
    if (searchTerm) params.search = searchTerm;
    if (firstName) params.firstName = firstName;
    if (lastName) params.lastName = lastName;
    if (email) params.email = email;
    if (phone) params.phone = phone;
    return params;
  }, [searchTerm, firstName, lastName, email, phone]);

  // Queries per mode
  const ownerClientsQuery = useUsersClientsByOwnerListQuery(
    { ownerId: Number(ownerId), ...searchParams },
    { skip: !ownerId || mode !== 'owner' }
  );

  const salonClientsQuery = useUsersClientsBySalonListQuery(
    { salonId: Number(selectedSalonId), ...searchParams },
    { skip: mode !== 'salon' || !selectedSalonId }
  );

  return (
    <Box sx={{ p: 2 }}>
      <Notification
        open={notificationState.open}
        message={notificationState.message}
        severity={notificationState.severity}
        onClose={() => setNotificationState((prev) => ({ ...prev, open: false }))}
      />
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, gap: 2, flexWrap: 'wrap' }}>
        <SectionHeader title={t('pro_clients.title')} />
        <Box sx={{ display: 'flex', gap: 2 }}>

          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleOpenAddClient}
            sx={{
              backgroundColor: theme.colors.primary[500],
              '&:hover': { backgroundColor: theme.colors.primary[600] },
              fontWeight: 600,
              textTransform: 'none'
            }}
          >
            {t('pro_clients.add_client')}
          </Button>

          <Tooltip
            title={selectedClientIds.length === 0 ? t('pro_clients.notify_tooltip') : ''}
            arrow
          >
            <span>
              <Button
                variant="contained"
                onClick={handleOpenNotifyDialog}
                disabled={selectedClientIds.length === 0}
                sx={{
                  backgroundColor: '#FF6B35',
                  '&:hover': { backgroundColor: '#E55A2B' },
                  '&.Mui-disabled': {
                    backgroundColor: '#FFB199',
                    color: '#FFFFFF',
                    opacity: 0.6
                  },
                  fontWeight: 600,
                  textTransform: 'none'
                }}
              >
                {t('pro_clients.notify_button')}{selectedClientIds.length > 0 ? ` (${selectedClientIds.length})` : ''}
              </Button>
            </span>
          </Tooltip>


          <ToggleButtonGroup
            value={mode}
            exclusive
            onChange={(_e, val) => val && setMode(val)}
            size="small"
          >
            <ToggleButton value="owner">{t('pro_clients.by_owner')}</ToggleButton>
            <ToggleButton value="salon">{t('pro_clients.by_salon')}</ToggleButton>
          </ToggleButtonGroup>
        </Box>
      </Box>

      {/* Search Fields */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <TextField
          size="small"
          label={t('pro_clients.search_label')}
          placeholder={t('pro_clients.search_placeholder')}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          sx={{ minWidth: 200 }}
        />
      </Box>

      {mode === 'owner' && (
        <Box>
          {ownerClientsQuery.isLoading || ownerClientsQuery.isFetching ? (
            <LoadingState />
          ) : (
            <ReactVirtualizedTable
              data={Array.isArray(ownerClientsQuery.data) ? ownerClientsQuery.data : []}
              columns={columns}
              height={500}
              showActions={false}
              selectable={true}
              selectedIds={selectedClientIds}
              onSelectionChange={setSelectedClientIds}
              onRowClick={setDetailsClient}
              rowIdKey="id"
            />
          )}
        </Box>
      )}

      {mode === 'salon' && (
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <FormControl size="small" sx={{ minWidth: 240 }}>
              <InputLabel id="salon-select-label">{t('pro_clients.select_salon')}</InputLabel>
              <Select
                labelId="salon-select-label"
                id="salon-select"
                label={t('pro_clients.select_salon')}
                value={selectedSalonId ?? ''}
                onChange={(e) => setSelectedSalonId(e.target.value)}
                disabled={salonsLoading || salonsFetching || salonOptions.length === 0}
              >
                {salonOptions.map((s) => (
                  <MenuItem key={s.salon_id} value={s.salon_id}>{s.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          {salonsLoading || salonsFetching ? (
            <LoadingState />
          ) : (
            <ReactVirtualizedTable
              data={Array.isArray(salonClientsQuery.data) ? salonClientsQuery.data : []}
              columns={columns}
              height={500}
              showActions={false}
              selectable={true}
              selectedIds={selectedClientIds}
              onSelectionChange={setSelectedClientIds}
              onRowClick={setDetailsClient}
              rowIdKey="id"
            />
          )}
        </Box>
      )}

      <ProfessionalClientDetailsDialog
        open={Boolean(detailsClient)}
        client={detailsClient}
        salonId={
          mode === 'salon' && selectedSalonId
            ? Number(selectedSalonId)
            : undefined
        }
        onClose={() => setDetailsClient(null)}
      />

      {/* Add Client Dialog */}
      <Dialog open={isAddClientOpen} onClose={handleCloseAddClient} maxWidth="sm" fullWidth>
        <form onSubmit={handleAddClientSubmit}>
          <DialogTitle>{t('pro_clients.add_client_dialog.title')}</DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
              {addClientError && (
                <Alert severity="error">{addClientError}</Alert>
              )}
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  autoFocus
                  required
                  label={t('pro_clients.add_client_dialog.first_name')}
                  fullWidth
                  value={newClientFirstName}
                  onChange={(e) => setNewClientFirstName(e.target.value)}
                />
                <TextField
                  required
                  label={t('pro_clients.add_client_dialog.last_name')}
                  fullWidth
                  value={newClientLastName}
                  onChange={(e) => setNewClientLastName(e.target.value)}
                />
              </Box>
              <TextField
                required
                label={t('pro_clients.add_client_dialog.email')}
                type="email"
                fullWidth
                value={newClientEmail}
                onChange={(e) => setNewClientEmail(e.target.value)}
                helperText={t('pro_clients.add_client_dialog.email_helper')}
              />
              <TextField
                required
                label={t('pro_clients.add_client_dialog.phone')}
                fullWidth
                value={newClientPhone}
                onChange={(e) => setNewClientPhone(e.target.value)}
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseAddClient}>{t('common.cancel')}</Button>
            <Button
              type="submit"
              variant="contained"
              disabled={isAddingClient}
              sx={{ backgroundColor: theme.colors.primary[500], '&:hover': { backgroundColor: theme.colors.primary[600] } }}
            >
              {isAddingClient ? <CircularProgress size={24} color="inherit" /> : t('common.add')}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Notify Clients Dialog */}
      <Dialog open={isNotifyDialogOpen} onClose={handleCloseNotifyDialog} maxWidth="md" fullWidth>
        <form onSubmit={handleNotifySubmit}>
          <DialogTitle>{t('pro_clients.notify_dialog.title', { count: selectedClientIds.length })}</DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
              {notifyError && (
                <Alert severity="error">{notifyError}</Alert>
              )}

              {/* Salon sender selection */}
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>{t('pro_clients.notify_dialog.sender_salon')}</Typography>
                <FormControl size="small" fullWidth disabled={salonOptions.length <= 1}>
                  <InputLabel id="notify-salon-select-label">{t('pro_clients.notify_dialog.sent_by')}</InputLabel>
                  <Select
                    labelId="notify-salon-select-label"
                    id="notify-salon-select"
                    label={t('pro_clients.notify_dialog.sent_by')}
                    value={notifySalonId ?? ''}
                    onChange={(e) => setNotifySalonId(e.target.value)}
                  >
                    <MenuItem value="">
                      <em>{salonOptions.length <= 1 ? t('pro_clients.notify_dialog.default_salon') : t('pro_clients.notify_dialog.choose_salon')}</em>
                    </MenuItem>
                    {salonOptions.map((s) => (
                      <MenuItem key={s.salon_id} value={s.salon_id}>{s.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                {salonOptions.length > 1 && !notifySalonId && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                    {t('pro_clients.notify_dialog.sender_required')}
                  </Typography>
                )}
              </Box>

              {/* Delivery Mode Selection */}
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>{t('pro_clients.notify_dialog.delivery_mode')}</Typography>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <input
                      type="checkbox"
                      checked={sendEmail}
                      onChange={(e) => setSendEmail(e.target.checked)}
                    />
                    {t('pro_clients.notify_dialog.send_email')}
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <input
                      type="checkbox"
                      checked={sendNotification}
                      onChange={(e) => setSendNotification(e.target.checked)}
                    />
                    {t('pro_clients.notify_dialog.send_notification')}
                  </label>
                </Box>
              </Box>

              {/* Email Fields */}
              {sendEmail && (
                <>
                  <TextField
                    required={sendEmail}
                    label={t('pro_clients.notify_dialog.email_subject')}
                    fullWidth
                    value={emailSubject}
                    onChange={(e) => setEmailSubject(e.target.value)}
                  />
                  <Box>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>{t('pro_clients.notify_dialog.email_content')}</Typography>
                    <Box
                      sx={{
                        border: '1px solid rgba(0,0,0,0.23)',
                        borderRadius: 1,
                        overflow: 'hidden',
                        '& .ql-toolbar': {
                          border: 'none',
                          borderBottom: '1px solid rgba(0,0,0,0.12)',
                        },
                        '& .ql-container': {
                          border: 'none',
                          height: 220,
                          overflowY: 'auto',
                        },
                      }}
                    >
                      <ReactQuill
                        theme="snow"
                        value={emailContent}
                        onChange={setEmailContent}
                        placeholder={t('pro_clients.notify_dialog.your_message')}
                        modules={quillModules}
                        formats={quillFormats}
                      />
                    </Box>
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                      {t('pro_clients.notify_dialog.email_caption')}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>{t('pro_clients.notify_dialog.photo_optional')}</Typography>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handlePhotoChange}
                      style={{ display: 'block' }}
                    />
                    {photo && (
                      <Typography variant="caption" sx={{ mt: 1, display: 'block' }}>
                        {t('pro_clients.notify_dialog.file_selected', { name: photo.name })}
                      </Typography>
                    )}
                  </Box>
                </>
              )}

              {/* Notification Fields */}
              {sendNotification && (
                <>
                  <TextField
                    required={sendNotification}
                    label={t('pro_clients.notify_dialog.notification_title')}
                    fullWidth
                    value={notificationTitle}
                    onChange={(e) => setNotificationTitle(e.target.value)}
                  />
                  <Box>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>{t('pro_clients.notify_dialog.notification_message')}</Typography>
                    <Box
                      sx={{
                        border: '1px solid rgba(0,0,0,0.23)',
                        borderRadius: 1,
                        overflow: 'hidden',
                        '& .ql-toolbar': {
                          border: 'none',
                          borderBottom: '1px solid rgba(0,0,0,0.12)',
                        },
                        '& .ql-container': {
                          border: 'none',
                          height: 160,
                          overflowY: 'auto',
                        },
                      }}
                    >
                      <ReactQuill
                        theme="snow"
                        value={notificationMessage}
                        onChange={setNotificationMessage}
                        placeholder={t('pro_clients.notify_dialog.notification_placeholder')}
                        modules={quillModules}
                        formats={quillFormats}
                      />
                    </Box>
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                      {t('pro_clients.notify_dialog.notification_caption')}
                    </Typography>
                  </Box>
                </>
              )}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseNotifyDialog}>{t('common.cancel')}</Button>
            <Button
              type="submit"
              variant="contained"
              disabled={isNotifying}
              sx={{ backgroundColor: '#FF6B35', '&:hover': { backgroundColor: '#E55A2B' } }}
            >
              {isNotifying ? <CircularProgress size={24} color="inherit" /> : t('pro_clients.notify_dialog.send_button')}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Box>
  );
}
